// CORE
import { useAppSelector } from 'hooks/redux';
import { Map as ImmutableMap } from 'immutable';
import React, { useCallback, useState, useMemo, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useEffectOnce, useToggle } from 'react-use';

// APP-COMPONENTS
import { accountDiscoveryActions, accountDiscoveryTypes } from 'app-components/flux/account-discovery';
import { accountsActions, accountsSelectors, accountsTypes, accountsUtils } from 'app-components/flux/accounts';
import { actionHelpers } from 'app-components/flux/core';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { institutionLoginsActions, institutionLoginsSelectors } from 'app-components/flux/institution-logins';
import { profileActions, profileSelectors } from 'app-components/flux/profile';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import { loadState, useLoadState } from 'app-components/hooks/utils';
import { getLogger, tracker } from 'app-components/utils/core';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNewRounded';
// MUI
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

// DATA
import * as fundingAccountsActions from 'data/fundingAccounts/actions';
import * as fundingAccountSelectors from 'data/fundingAccounts/selectors';
import {
  FundingAccountOwnerTypeEnum,
  type FundingAccountType,
  type VerificationStatus,
  mkFundingAccount,
  verificationStatusEnum,
} from 'data/fundingAccounts/types';

// COMPONENTS
import bankBuilding from 'assets/illustrations/Bank-Building.png';
import MoneyInBankImage from 'assets/illustrations/Money-In-Bank.svg';
import paperAirplaneCrash from 'assets/illustrations/Plane-Crash.png';
import GreenThumbImage from 'assets/illustrations/Thumbs-up-star.png';

import InstitutionErrorBanner from 'components/AccountCard/InstitutionErrorBanner';
import type { OnboardAccountsForTransfersDialogProps } from 'components/Dialogs/A2A/actions';
import {
  showInitiateTransferDialog,
  showMicrodespositDialog,
  showOnboardAccountsDialog,
} from 'components/Dialogs/A2A/actions';
import SelectAccountForm from 'components/Dialogs/A2A/components/SelectAccountForm';
import TransferAccountRow from 'components/Dialogs/A2A/components/TransferAccountRow';
import TransferInstitutionRow from 'components/Dialogs/A2A/components/TransferInstitutionRow';
import { DIALOG_TYPE as DIALOG_CONFIRMATION, mkConfirmationDialogProps } from 'components/Dialogs/ConfirmationDialog';
import SetupPage from 'components/Dialogs/IdentityVerification/SetupPage';
import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import Dump from 'components/Dump';
import InstitutionIcon from 'components/InstitutionIcon';
import LoadingView from 'components/LoadingView';
import QButton from 'components/QButton';
import ZeroStateView from 'components/ZeroStateView';

// LOCAL
import { type onboardA2ADialogIndex, onboardA2ADialogIndexEnums } from './onboardA2ADialogIndexEnums';

const log = getLogger('components/Dialogs/A2A/OnboardAccountsForTransfers');

const useStyles = makeStyles()((theme) => ({
  termsLink: {
    textDecorationLine: 'none',
    color: theme.palette.primary.main,
    display: 'inline-flex',
    alignItems: 'center',
    '&:hover': {
      textDecorationLine: 'underline',
    },
  },
  errorTab: {
    padding: `${theme.spacing(8)} ${theme.spacing(15)}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  errorWrapper: {
    color: 'inherit',
    fontSize: 'inherit',
  },
  errorCodeText: {
    color: 'inherit',
    fontStyle: 'italic',
    fontSize: 'inherit',
  },
  viewOneWrapper: {
    overflowX: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
    overflowY: 'auto',
  },
  institutionSelectWrapper: {
    paddingRight: 20,
    width: '100%',
    marginLeft: 10,
    display: 'flex',
    flexDirection: 'column',
  },
  instituteIconWrapper: {
    minWidth: 64,
  },
  accountFormWrapper: {
    marginTop: 20,
    marginLeft: -70,
    paddingRight: 20,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  moneyColumn: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  transferMoneyIcon: {},
  accountEligibleTitle: {
    marginTop: 8,
  },
  accountEligibleText: {
    marginTop: 20,
  },
  accountEligibleNumber: {
    marginBottom: 10,
    color: theme.palette.text.secondary,
  },
  secondaryText: {
    color: theme.palette.text.secondary,
  },
  secondaryTextItem: {
    color: theme.palette.text.secondary,
    textAlign: 'center',
    width: 600,
    marginLeft: 10,
  },
  buttonWrapper: {
    paddingBottom: 20,
  },
  endButton: {
    marginTop: theme.spacing(2),
    float: 'left',
  },
  accountWrapper: {
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    marginTop: 10,
    marginBottom: 20,
  },
  accountDetailsHeader: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  divider: {
    marginTop: 15,
    marginBottom: 15,
  },
  insideWrapper: {
    width: 350,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingLeft: theme.spacing(1),
    marginBottom: 30,
  },
  accountNumberWrapper: {
    marginTop: 10,
    marginBottom: 20,
    marginRight: 20,
  },
  detailsWrapper: {
    marginTop: 5,
    marginLeft: 10,
  },
  finalImage: {
    marginBottom: 40,
    width: 120,
  },
  errorBanner: {
    margin: '0px 0px 0px 0px',
  },
  pendingTitle: {
    marginBottom: 16,
  },
  marginBottom: {
    marginBottom: theme.spacing(1),
  },
  marginBottomTwo: {
    marginBottom: theme.spacing(2),
  },
  transferAccountRow: {
    marginBottom: 0,
  },
  checkbox: {
    marginBottom: 20, // This is so we align the checkbox to the start of the text
  },
  successTab: {
    padding: `${theme.spacing(8)} ${theme.spacing(15)}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  thumbsUp: {
    width: 168,
  },
  bold: {
    fontWeight: 500,
  },
  titleWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  warningIcon: {
    color: theme.palette.warning.main,
    marginRight: 8,
  },
}));

//  ====================== VALIDATION FUNCTIONS ===========================

//  Ensure that the routing number is legitimate
const validateRoutingNumber = (routingNumber: string) => {
  const digits = routingNumber?.split('')?.map((d) => parseInt(d, 10));
  const checksum =
    3 * (digits[0] + digits[3] + digits[6]) +
    7 * (digits[1] + digits[4] + digits[7]) +
    (digits[2] + digits[5] + digits[8]);

  return checksum !== 0 && checksum % 10 === 0;
};

type AccountInfo = {
  id: string;
  accountNumber?: string;
  accountNumberConfirm?: string;
  routingNumber?: string;
  fundingAccountId?: string;
};
type FormErrors = {
  accountNumber?: string;
  accountNumberConfirm?: string;
  routingNumber?: string;
};

// returns object like selectedAccounts but error messages instead of values like
//   { [accountId]: { accountNumber: <error message>, routingNumber: <error message> } }
//   or null if no errors
const validateSelectedAccount = (selectedAccountValidationObj: AccountInfo | null) => {
  const acc = selectedAccountValidationObj;
  const accountErrors: FormErrors = {};
  if (!acc?.routingNumber || acc?.routingNumber.length !== 9) {
    accountErrors.routingNumber = !acc?.routingNumber
      ? 'Routing number is required'
      : 'Routing number must be 9 digits';
  } else if (!validateRoutingNumber(acc?.routingNumber || '')) {
    accountErrors.routingNumber = 'Only U.S.-based accounts are eligible for transfers';
  }
  if (!acc?.accountNumber) {
    accountErrors.accountNumber = 'Account number is required';
  } else if (acc?.accountNumber?.length < 7) {
    accountErrors.accountNumber = 'Please enter a valid account number';
  }
  if (acc?.accountNumber !== acc?.accountNumberConfirm) {
    accountErrors.accountNumberConfirm = 'Account numbers do not match';
  }

  return Object.keys(accountErrors).length > 0 ? accountErrors : null;
};

const isMicrodepositSentStatus = (status?: VerificationStatus) => {
  if (
    status &&
    (status === verificationStatusEnum.MICRO_DEPOSIT_INITIATED ||
      status === verificationStatusEnum.MICRO_DEPOSIT_POSTED ||
      status === verificationStatusEnum.MICRO_DEPOSIT_VERIFICATION_REQUIRED ||
      status === verificationStatusEnum.MICRO_DEPOSIT_VERIFICATION_AMOUNTS_INVALID ||
      status === verificationStatusEnum.MICRO_DEPOSIT_VERIFICATION_FAILED)
  ) {
    return true;
  }
  return false;
};

const getFinalFourFormatted = (account?: accountsTypes.Account | null, includeName?: boolean) => {
  if (!account) {
    log.warn('Account missing in final4 lookup');
    return '????';
  }
  if (includeName) {
    return `${account?.name} (${accountsUtils.formatFinalFour(account, true)})`;
  }
  const accountSubtitle = `${
    accountsTypes.getAccountTypeNodeByQcsType(account.type, account.subType).displayName
  } ${accountsUtils.formatFinalFour(account)}`;
  return accountSubtitle;
};

//  ======================== MAIN ========================

const OnboardAccountsForTransfersDialog = React.memo((props: OnboardAccountsForTransfersDialogProps) => {
  const isMounted = useRef<boolean>();
  const { dialogId, onClose, startTabIndex, initialInstitutionId, initialAccountId, initiator, overrideOpening } =
    props;
  // startTabIndex is the tab that is the "starting point" after the opening screen. We assume the opening screen will
  // always show unless we specifically override it.
  const dispatch = useDispatch();
  const { classes } = useStyles();

  //  =================== SELECTORS ========================
  const isEmpty = useSelector(fundingAccountSelectors.areNoAccountsEligible);
  const activeIdentity = useSelector(profileSelectors.getIdentityVerified);

  const eligibleTransferAccountsList = useSelector(fundingAccountSelectors.getEligibleTransferAccountsList);

  const hasFundingAccounts = useSelector(fundingAccountSelectors.getHasFundingAccounts);
  const institutionLogins = useSelector(institutionLoginsSelectors.getInstitutionLoginsById);
  const verifiedPendingFundingAccounts = useSelector(
    fundingAccountSelectors.getAllPendingAndVerifiedFundingAccountsByAccountId,
  );

  // ================== SELECTIONS ====================
  const [selectedInstitutionId, setSelectedInstitutionId] = useState<string | null>(initialInstitutionId || null);
  const [selectedAccountValidationObj, setSelectedAccountValidationObj] = useState<AccountInfo | null>(
    initialAccountId ? { id: initialAccountId } : null,
  );

  // =================== ACCOUNT + INSTITUTION CALLBACKS ============================

  const selectedGroup = useMemo(() => {
    return selectedInstitutionId
      ? eligibleTransferAccountsList.find((group) => group?.institution?.id === selectedInstitutionId)
      : null;
  }, [eligibleTransferAccountsList, selectedInstitutionId]);

  /* Gets the account object based on the selectedAccountValidationObj id */
  const currentAccount = useAppSelector((state) =>
    accountsSelectors.getAccountById(state, selectedAccountValidationObj?.id),
  );
  const currentFundingAccount = useAppSelector((state) =>
    fundingAccountSelectors.getFundingAccountById(state, currentAccount?.fundingAccountId),
  );
  const a2AInvestments = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlag(state, 'a2AInvestmentAccounts'),
  );

  const isFirstTime = verifiedPendingFundingAccounts?.isEmpty() && !overrideOpening;

  //  ===================== STATES ============================
  const [tabIndex, setTabIndex] = useState<onboardA2ADialogIndex>(
    isFirstTime ? onboardA2ADialogIndexEnums.OPENING : startTabIndex || onboardA2ADialogIndexEnums.SELECT_INST,
  );
  const [fieldErrors, setFieldErrors] = useState<FormErrors | null>(null);
  const [agreedToTerms, toggleAgreedToTerms] = useToggle(false);

  const [errorCode, setErrorCode] = useState(null);
  const [errorMessage, setErrorMessage] = useState('Please try again at another time.');

  //  ================= VIEW STATE LOGIC =====================
  //  Views
  const fundingView = useLoadState(fundingAccountSelectors);
  const accountsView = useLoadState(accountsSelectors);
  const profileView = useLoadState(profileSelectors);
  const instLoginView = useLoadState(institutionLoginsSelectors);
  //  We do not include 'institutions' because it only handles retrieving the logo, and that is not crucial

  const mainView = useMemo(() => {
    const showErrorView = tabIndex === onboardA2ADialogIndexEnums.ERROR;

    const showSkeletonView = Boolean(
      loadState.skeletonStates.includes(fundingView) ||
        loadState.skeletonStates.includes(profileView) ||
        loadState.skeletonStates.includes(instLoginView) ||
        loadState.skeletonStates.includes(accountsView) ||
        tabIndex === onboardA2ADialogIndexEnums.LOADING,
    );

    // For some reason, the tryAgainStates must be separated into a separate const in order to prevent a lint error.
    const tryAgainStates = Boolean(
      loadState.tryAgainStates.includes(fundingView) ||
        loadState.tryAgainStates.includes(accountsView) ||
        loadState.tryAgainStates.includes(profileView) ||
        loadState.tryAgainStates.includes(instLoginView),
    );
    const showTryAgainView = Boolean(
      (tryAgainStates ||
        loadState.updateRequiredStates.includes(fundingView) ||
        loadState.updateRequiredStates.includes(accountsView) ||
        loadState.updateRequiredStates.includes(profileView) ||
        loadState.updateRequiredStates.includes(instLoginView)) &&
        !isEmpty &&
        tabIndex !== onboardA2ADialogIndexEnums.LOADING,
    );

    //  For some reason the noDataState is broken for fundingView, so it will be included in showReadyView and 'isEmpty' will be used to determine if there is no fundingData instead
    const showNoDataView = Boolean(
      (loadState.noDataStates.includes(accountsView) ||
        loadState.noDataStates.includes(profileView) ||
        loadState.noDataStates.includes(instLoginView) ||
        isEmpty) &&
        tabIndex !== onboardA2ADialogIndexEnums.LOADING,
    );
    const showReadyView = Boolean(
      (loadState.readyStates.includes(fundingView) || loadState.noDataStates.includes(fundingView)) &&
        loadState.readyStates.includes(accountsView) &&
        loadState.readyStates.includes(profileView) &&
        loadState.readyStates.includes(instLoginView) &&
        !isEmpty &&
        tabIndex !== onboardA2ADialogIndexEnums.LOADING,
    );

    let myMainView = '';
    if (showErrorView) {
      myMainView = 'ERROR';
    } else if (showReadyView) {
      myMainView = 'READY';
    } else if (showNoDataView) {
      myMainView = 'EMPTY';
    } else if (showTryAgainView) {
      myMainView = 'FAILED';
    } else if (showSkeletonView) {
      myMainView = 'LOADING';
    }

    //  OVERRIDE mainView for unique tabIndex exceptions
    // Exception 1: TabIndex === SUCCESS: Technically, there are no available accounts eligible to be setup for transfers in the event
    //              where the user has finished the verification process for ALL available accounts, however, we do not want to show the
    //              empty state screen, we want to show the success screen! We only want to show the empty state screen once the user
    //              has exited the dialog and then later tries to add more accounts (When none are eligible)
    // Exception 2: When the widget first loads in (With tabIndex === ACCT_INFO) there is a small period of time where the account is marked as
    //              ineligible and the funding account does not exist. This is because QCS is still loading changes. During this time, technically
    //              we meet the conditions to show a 0 state, however, as QCS is still loading data, we do not want to show the empty state
    //              screen, so we avoid doing so with this exception.
    if (
      (tabIndex === onboardA2ADialogIndexEnums.SUCCESS || tabIndex === onboardA2ADialogIndexEnums.ACCT_INFO) &&
      myMainView === 'EMPTY'
    ) {
      myMainView = 'READY';
    }
    return myMainView;
  }, [accountsView, fundingView, instLoginView, isEmpty, profileView, tabIndex]);

  /*
  ERROR
  LOADING
  OPENING <- First
  SELECT_INST <- Second
  SELECT_ACCT <- Third
  ACCT_INFO <- Fourth
  SUBMIT <- Fifth
  SUCCESS <- Final
  */
  // =============== TAB INDEX CONTROL ==================
  const decTabIndex = () => {
    switch (tabIndex) {
      case onboardA2ADialogIndexEnums.SELECT_INST:
        setTabIndex(onboardA2ADialogIndexEnums.OPENING);
        break;
      case onboardA2ADialogIndexEnums.SELECT_ACCT:
        setTabIndex(onboardA2ADialogIndexEnums.SELECT_INST);
        break;
      case onboardA2ADialogIndexEnums.ACCT_INFO:
        setFieldErrors(null);
        setTabIndex(onboardA2ADialogIndexEnums.SELECT_ACCT);
        break;
      case onboardA2ADialogIndexEnums.SUBMIT:
        setTabIndex(onboardA2ADialogIndexEnums.ACCT_INFO);
        break;
      default:
    }
  };

  const showBackButton =
    tabIndex !== onboardA2ADialogIndexEnums.SUCCESS &&
    tabIndex !== onboardA2ADialogIndexEnums.OPENING &&
    tabIndex !== onboardA2ADialogIndexEnums.LOADING &&
    tabIndex !== onboardA2ADialogIndexEnums.ERROR &&
    tabIndex !== onboardA2ADialogIndexEnums.FAILURE &&
    !(tabIndex === onboardA2ADialogIndexEnums.SELECT_INST && !isEmpty);

  // ============== ON ERROR ========================
  const onError = useCallback(
    (trackerEvent: string, error: any) => {
      tracker.track(trackerEvent, {
        error: error?.response?.data?.errors[0]?.detail || error?.response?.data?.errors[0]?.title,
      });
      if (isMounted.current === true) {
        setErrorCode(error?.response?.status);
        setErrorMessage(error?.response?.data?.errors[0]?.detail || error?.response?.data?.errors[0]?.title);
        setTabIndex(onboardA2ADialogIndexEnums.FAILURE);
      }
    },
    [setTabIndex],
  );

  //  ================== EFFECTS ===================

  // track mounted state because of async promise
  useEffect(() => {
    dispatch(profileActions.getProfile());
    dispatch(fundingAccountsActions.getFundingAccounts()); //  Sometimes QCS returns funding accounts as null so this is an added 'get' to be safe? Maybe it will solve it?
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // On page open only
  useEffectOnce(() => {
    if (tabIndex === onboardA2ADialogIndexEnums.OPENING) {
      tracker.track(tracker.events.viewTransfersIntroScreen, { initiator: 'account verification' });
    }

    tracker.track(tracker.events.viewAccountSetup, { initiator });
  });

  useEffect(() => {
    // TODO: maybe don't dispatch tracking events in a useEffect?
    if (tabIndex === onboardA2ADialogIndexEnums.ACCT_INFO) {
      tracker.track(tracker.events.enterAccountDetails);
    }
  }, [currentFundingAccount, tabIndex]);

  // =================== CALLBACKS ==================
  const kickoffA2AFlow = useCallback(
    (_e: unknown, startTabIndex: any) => {
      dispatch(
        showOnboardAccountsDialog({
          startTabIndex: startTabIndex || onboardA2ADialogIndexEnums.SELECT_INST,
          initialInstitutionId: selectedGroup?.institution?.id,
          overrideOpening: true,
          initiator,
        }),
      );
    },
    [dispatch, selectedGroup?.institution?.id, initiator],
  );

  const getFundingAccounts = useCallback(() => {
    dispatch(fundingAccountsActions.getFundingAccounts());
    dispatch(accountsActions.getAccounts());
    dispatch(profileActions.getProfile());
    dispatch(institutionLoginsActions.getInstitutionLogins());
  }, [dispatch]);

  const handleClose = useCallback(() => {
    dispatch(rootUiActions.removeDialog(dialogId));
    onClose?.();
  }, [dialogId, dispatch, onClose]);

  const kickoffMicrodepositFlow = useCallback(
    (accountInfo: AccountInfo) => {
      handleClose();
      dispatch(
        showMicrodespositDialog({
          fundingAccountId: accountInfo?.fundingAccountId,
          hideAccountPicker: true,
          initiator: 'onboarding flow',
        }),
      );
    },
    [dispatch, handleClose],
  );

  const handleRequestCreate = useCallback(() => {
    const loginId = (selectedGroup?.eligible || selectedGroup?.others)?.[0]?.institutionLoginId;
    const existingLogin = loginId && institutionLogins.get(loginId);
    handleClose();
    if (existingLogin) {
      dispatch(
        accountDiscoveryActions.doAccountRediscovery(existingLogin, {
          dialogTitle: 'Add account',
          onClose: () => kickoffA2AFlow(null, onboardA2ADialogIndexEnums.SELECT_ACCT),
        }),
      );
    } else {
      dispatch(
        accountDiscoveryActions.doAccountDiscovery({
          initiator: 'manual accounts',
          mode: 'settings accounts link',
        }),
      );
    }
  }, [dispatch, handleClose, institutionLogins, kickoffA2AFlow, selectedGroup]);

  const openWarningDialog = useCallback(() => {
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'numbers-dont-match',
          type: DIALOG_CONFIRMATION,
          allowNesting: true,
          props: ImmutableMap(
            mkConfirmationDialogProps({
              size: 'xs',
              title: (
                <div className={classes.titleWrapper}>
                  <WarningRoundedIcon className={classes.warningIcon} />
                  <Typography variant='titleMedium'>Account number mismatch</Typography>
                </div>
              ),
              content: (
                <div>
                  <Typography variant='titleRegular' className={classes.marginBottomTwo}>
                    The last four digits of the account number you entered don’t match the last four digits of your
                    connected account (
                    <span className={classes.bold}>{accountsUtils.formatFinalFour(currentAccount)}</span>).
                  </Typography>
                  <Typography variant='titleRegular'>
                    Please check that the account number entered matches the account you’re trying to enable before
                    continuing.
                  </Typography>
                </div>
              ),
              confirmLabel: 'Continue',
              denyLabel: 'Cancel',
              confirmAction: () => {
                setTabIndex(onboardA2ADialogIndexEnums.SUBMIT);
              },
            }),
          ),
        }),
      ),
    );
  }, [classes, currentAccount, dispatch]);

  const handleErrorsOnContinue = useCallback(() => {
    if (!currentAccount) return;
    const lastFourDigits = accountsUtils.formatFinalFour(currentAccount).slice(-4);
    const errors = validateSelectedAccount(selectedAccountValidationObj);
    if (errors) {
      setFieldErrors(errors);
    } else {
      setFieldErrors(null);
      if (
        selectedAccountValidationObj?.accountNumber &&
        lastFourDigits &&
        selectedAccountValidationObj?.accountNumber.slice(
          selectedAccountValidationObj?.accountNumber.length - lastFourDigits.length,
        ) !== lastFourDigits
      ) {
        openWarningDialog();
      } else {
        setTabIndex(onboardA2ADialogIndexEnums.SUBMIT);
      }
    }
  }, [openWarningDialog, currentAccount, selectedAccountValidationObj, setTabIndex]);

  // If there is ANY change in a field done by a user, clear out the error in that field. We will show what the new error is on submit
  const checkErrorDiff = useCallback(
    (newAccount: AccountInfo) => {
      const errors = validateSelectedAccount(newAccount);
      const newFieldErrors: FormErrors = { ...fieldErrors };

      if (errors?.accountNumber !== newFieldErrors?.accountNumber) {
        newFieldErrors.accountNumber = undefined;
      }
      if (errors?.accountNumberConfirm !== newFieldErrors?.accountNumberConfirm) {
        newFieldErrors.accountNumberConfirm = undefined;
      }
      if (errors?.routingNumber !== newFieldErrors?.routingNumber) {
        newFieldErrors.routingNumber = undefined;
      }

      return newFieldErrors;
    },
    [fieldErrors],
  );

  const handleChange = useCallback(
    (newAccount: AccountInfo) => {
      if (!newAccount) {
        setSelectedAccountValidationObj(null);
      } else {
        const updatedErrors = checkErrorDiff(newAccount);
        setFieldErrors(updatedErrors);
        setSelectedAccountValidationObj(newAccount);
      }
    },
    [checkErrorDiff],
  );

  const handleSubmitForm = useCallback(() => {
    setTabIndex(onboardA2ADialogIndexEnums.LOADING);
    if (!currentAccount) return;

    //  Map MONEY_MARKET to SAVINGS. Map CASH_MANAGEMENT and INVESTMENT types to CHECKING. We want to allow MONEY_MARKET/CASH_MANAGEMENT accounts.
    //  QCS FundingAccounts cannot handle the subtype, so we change it to SAVINGS/CHECKING.
    //  I am not sure why it is not done on the QCS end, but apparently it is difficult, so it will be done here for now.
    let bankType = currentAccount.subType;
    if (bankType === 'CASH_MANAGEMENT' || currentAccount.type === 'INVESTMENT') {
      bankType = 'CHECKING';
    } else if (bankType === 'MONEY_MARKET') {
      bankType = 'SAVINGS';
    }

    const payload = mkFundingAccount({
      routingNumber: selectedAccountValidationObj?.routingNumber,
      accountNumber: selectedAccountValidationObj?.accountNumber,
      accountType: bankType as FundingAccountType,
      ownerType: FundingAccountOwnerTypeEnum.PERSONAL,
      nickname: currentAccount.name,
      linkedAccountId: currentAccount.id ?? undefined,
      destinationOnly: currentAccount.type === 'INVESTMENT',
    });

    actionHelpers
      .bindPromiseAction(
        dispatch,
        fundingAccountsActions.createFundingAccount,
      )(payload)
      .then((resp: any) => {
        if (isMounted.current === true) {
          getFundingAccounts();
          toggleAgreedToTerms();
          if (resp?.verificationStatus === verificationStatusEnum.MICRO_DEPOSIT_VERIFICATION_COMPLETED) {
            tracker.track(tracker.events.accountSetupCompleted, { result: 'account enabled' });
            setTabIndex(onboardA2ADialogIndexEnums.READY_TO_TRANSFER);
          } else if (resp?.verificationStatus === verificationStatusEnum.MICRO_DEPOSIT_VERIFICATION_FAILED) {
            kickoffMicrodepositFlow({ id: currentAccount?.id || '', fundingAccountId: resp?.id });
          } else if (isMicrodepositSentStatus(resp?.verificationStatus)) {
            tracker.track(tracker.events.accountSetupCompleted, { result: 'micro deposit sent' });
            setTabIndex(onboardA2ADialogIndexEnums.SUCCESS);
          } else if (resp?.verificationStatus === verificationStatusEnum.MICRO_DEPOSIT_FAILED) {
            tracker.track(tracker.events.accountSetupError, {
              error: 'Failed to deposit amounts to account',
            });
            setTabIndex(onboardA2ADialogIndexEnums.FAILURE);
          }
        }
      })
      .catch((error) => {
        onError(tracker.events.accountSetupError, error);
      });
  }, [
    kickoffMicrodepositFlow,
    selectedAccountValidationObj?.routingNumber,
    selectedAccountValidationObj?.accountNumber,
    currentAccount,
    dispatch,
    getFundingAccounts,
    toggleAgreedToTerms,
    onError,
  ]);

  const handleContinue = useCallback(
    (obj: any) => {
      switch (tabIndex) {
        case onboardA2ADialogIndexEnums.OPENING:
          // ACCT_INFO is not a starting index option
          /* if (startTabIndex === onboardA2ADialogIndexEnums.ACCT_INFO && selectedAccountValidationObj) {
            setTabIndex(onboardA2ADialogIndexEnums.ACCT_INFO);
          } else { */
          setTabIndex(startTabIndex || onboardA2ADialogIndexEnums.SELECT_INST);
          // }
          break;
        case onboardA2ADialogIndexEnums.SELECT_INST:
          tracker.track(tracker.events.institutionSelect, {
            number: eligibleTransferAccountsList?.size,
            institution_name: obj?.institution?.name,
          });
          setSelectedInstitutionId(obj?.institution?.id);
          setTabIndex(onboardA2ADialogIndexEnums.SELECT_ACCT);
          break;
        case onboardA2ADialogIndexEnums.SELECT_ACCT:
          tracker.track(tracker.events.accountSelect, {
            number: selectedGroup?.eligible?.length,
            status: 'eligible',
            type: accountsTypes?.getAccountTypeNodeByQcsType(obj?.type, obj?.subType)?.displayName.toLowerCase(),
          });
          setTabIndex(onboardA2ADialogIndexEnums.ACCT_INFO);
          break;
        case onboardA2ADialogIndexEnums.ACCT_INFO:
          handleErrorsOnContinue();
          break;
        case onboardA2ADialogIndexEnums.SUBMIT:
          handleSubmitForm();
          break;
        case onboardA2ADialogIndexEnums.SUCCESS:
          handleClose();
          break;
        case onboardA2ADialogIndexEnums.FAILURE:
          handleClose();
          break;
        default:
          setTabIndex(onboardA2ADialogIndexEnums.FAILURE);
          break;
      }
    },
    [
      handleClose,
      handleErrorsOnContinue,
      handleSubmitForm,
      eligibleTransferAccountsList,
      selectedGroup,
      setTabIndex,
      startTabIndex,
      tabIndex,
    ],
  );

  const handleOpenAccountDiscovery = useCallback(() => {
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'select-accounts-for-transfers-add-account',
          type: accountDiscoveryTypes.DIALOG_TYPE_ACCOUNTS_DISCOVERY,
          allowNesting: false,
          props: ImmutableMap(
            accountDiscoveryTypes.mkAccountDiscoveryDialogProps({
              initiator: 'select-accounts-for-transfer',
              mode: 'ADD-FI',
            }),
          ),
        }),
      ),
    );
    handleClose();
  }, [dispatch, handleClose]);

  const kickoffTransferFlow = (_e?: any) => {
    handleClose();
    dispatch(
      showInitiateTransferDialog({
        initiator: 'onboarding accounts success screen',
      }),
    );
  };

  const handleOpenTerms = (e: any) => {
    e.stopPropagation(); // Prevent event from bubbling up to the checkbox
    e.preventDefault(); // Prevent default behavior
    window.open('https://www.quicken.com/simplifi/terms', '_blank');
  };

  // =============== DERIVED VALUES =================
  let dialogTitle = 'Set up account transfers';
  if (tabIndex === onboardA2ADialogIndexEnums.ERROR) dialogTitle = 'Enabling transfers failed';
  if (tabIndex === onboardA2ADialogIndexEnums.SELECT_INST) dialogTitle = 'Select institution';
  if (tabIndex === onboardA2ADialogIndexEnums.SELECT_ACCT) dialogTitle = 'Set up account transfers';
  if (tabIndex === onboardA2ADialogIndexEnums.ACCT_INFO) dialogTitle = 'Enter account info';
  if (tabIndex === onboardA2ADialogIndexEnums.SUBMIT) dialogTitle = 'Authorize to transfer money';
  if (tabIndex === onboardA2ADialogIndexEnums.SUCCESS) dialogTitle = 'Verification deposits sent';
  if (tabIndex === onboardA2ADialogIndexEnums.FAILURE) dialogTitle = 'Account verification failed';

  let disableContinue = false;

  if (tabIndex === onboardA2ADialogIndexEnums.SUBMIT && !agreedToTerms) {
    disableContinue = true;
  }

  // verify that we have at least 1 valid accounts and no fieldErrors (FieldErrors should be allowed since they are only set by clicking continue, so cannot be unset unless continue is clickable)
  if (tabIndex === onboardA2ADialogIndexEnums.ACCT_INFO && !hasFundingAccounts && !selectedAccountValidationObj) {
    disableContinue = true;
  }
  if (tabIndex === onboardA2ADialogIndexEnums.ACCT_INFO && !selectedAccountValidationObj) {
    disableContinue = true;
  }
  if (tabIndex === onboardA2ADialogIndexEnums.SUBMIT && !agreedToTerms) {
    disableContinue = true;
  }
  if (!activeIdentity) {
    disableContinue = true;
  }

  let continueText = 'continue';
  if (tabIndex === onboardA2ADialogIndexEnums.OPENING) continueText = 'get started';
  if (tabIndex === onboardA2ADialogIndexEnums.SUBMIT) continueText = 'confirm';
  if (tabIndex === onboardA2ADialogIndexEnums.SUCCESS) continueText = 'done';
  if (tabIndex === onboardA2ADialogIndexEnums.FAILURE) continueText = 'close';

  // =============== RENDER FUNCTIONS =================

  const showEmptyView = useMemo(() => {
    let secondaryText =
      "To transfer money in Simplifi, you'll first need to connect a U.S. based, personal checking or savings account.";

    if (loadState.noDataStates.includes(profileView)) {
      secondaryText += ' You must have a validate your identity to transfer money between accounts';
    }

    return (
      <ZeroStateView
        className={classes.errorTab}
        id='zero-state'
        primary={
          <Typography className={classes.marginBottomTwo} variant='headlineMedium'>
            No eligible accounts added yet
          </Typography>
        }
        secondaryVariant='bodyRegular'
        secondary={secondaryText}
        icon={paperAirplaneCrash}
        button={
          <QButton onClick={handleOpenAccountDiscovery} variant='contained' startIcon={<AddCircleIcon />}>
            account
          </QButton>
        }
      />
    );
  }, [classes, handleOpenAccountDiscovery, profileView]);

  /* After clicking on an institution, this renders the list of accounts shown to the user */
  const renderAccountListForInstitution = useMemo(() => {
    if (!selectedGroup) {
      return {
        eligibleAccounts: [],
        ineligibleAccounts: [],
      };
    }

    const eligibleAccounts =
      selectedGroup?.eligible?.map((account, index) => (
        <TransferAccountRow
          className={classes.transferAccountRow}
          key={account.id}
          account={account}
          index={index}
          kickoffMicrodepositFlow={kickoffMicrodepositFlow}
          handleContinue={() => handleContinue(account)}
          handleChange={handleChange}
          formatFinalFour={getFinalFourFormatted}
        />
      )) || [];

    if (selectedGroup?.enabled?.length) {
      selectedGroup?.enabled.forEach((account, index) => {
        eligibleAccounts.push(
          <TransferAccountRow
            className={classes.transferAccountRow}
            key={account.id}
            account={account}
            index={index}
            kickoffMicrodepositFlow={kickoffMicrodepositFlow}
            handleContinue={() => handleContinue(account)}
            handleChange={handleChange}
            formatFinalFour={getFinalFourFormatted}
          />,
        );
      });
    }

    const ineligibleAccounts =
      selectedGroup?.others?.map((account, index) => (
        <TransferAccountRow
          className={classes.transferAccountRow}
          key={account.id}
          account={account}
          index={index}
          kickoffMicrodepositFlow={kickoffMicrodepositFlow}
          handleContinue={handleContinue}
          handleChange={handleChange}
          formatFinalFour={getFinalFourFormatted}
        />
      )) || [];

    const accountObj = {
      eligibleAccounts,
      ineligibleAccounts,
    };

    return accountObj;
  }, [classes.transferAccountRow, handleChange, handleContinue, selectedGroup, kickoffMicrodepositFlow]);

  const getInstitutionLoginInErrorByInstId = (institutionId: string) =>
    institutionLogins.find(
      (login: any) =>
        login.institutionId === institutionId &&
        login?.aggregators?.first()?.aggStatus !== 'OK' &&
        login?.aggregators?.first()?.aggStatus !== null,
    ) || null;

  return (
    <StdDialog
      dialogId={dialogId}
      disableBackdropClick={tabIndex === onboardA2ADialogIndexEnums.LOADING}
      onBack={() => {
        if (agreedToTerms && tabIndex === onboardA2ADialogIndexEnums.SUBMIT) toggleAgreedToTerms();
        decTabIndex();
      }}
      onClose={handleClose}
      sharedcomponentid='DIALOG_TYPE_SELECT_ACCOUNTS_FOR_TRANSFERS'
      showBackButton={showBackButton}
      showCloseButton={tabIndex !== onboardA2ADialogIndexEnums.LOADING}
      size='md'
      title={dialogTitle}
    >
      <StdDialogContent>
        {
          // startup page that only appears if it is the users first time
          tabIndex === onboardA2ADialogIndexEnums.OPENING && mainView === 'READY' && <SetupPage />
        }
        {
          // select institution
          tabIndex === onboardA2ADialogIndexEnums.SELECT_INST && mainView === 'READY' && (
            <div className={classes.viewOneWrapper}>
              <Dump obj={eligibleTransferAccountsList} />
              <div className={classes.moneyColumn}>
                <img
                  src={bankBuilding}
                  alt='transfer-cards'
                  className={classes.transferMoneyIcon}
                  width='168px'
                  height='auto'
                />
              </div>
              <div className={classes.institutionSelectWrapper}>
                <Typography variant='bodyRegular'>
                  Select the financial institution you want to enable transfers for.
                </Typography>
                <Typography className={classes.marginBottom} color='text.secondary' variant='paragraphRegular'>
                  {a2AInvestments
                    ? 'Only U.S.-based, personal checking, savings and investment accounts are eligible.'
                    : 'Only U.S.-based, personal checking and savings accounts are eligible.'}
                </Typography>
                {eligibleTransferAccountsList.map((institutionGroup, index) => (
                  <TransferInstitutionRow
                    key={institutionGroup?.institution?.id}
                    index={index}
                    handleContinue={handleContinue}
                    institutionGroup={institutionGroup}
                    institutionLogin={getInstitutionLoginInErrorByInstId(
                      institutionGroup?.institution?.get('id') || '',
                    )}
                  />
                ))}
              </div>
            </div>
          )
        }
        {
          // select account
          tabIndex === onboardA2ADialogIndexEnums.SELECT_ACCT && mainView === 'READY' && (
            <div className={classes.viewOneWrapper}>
              <div className={classes.instituteIconWrapper}>
                <InstitutionIcon institution={selectedGroup?.institution || null} />
              </div>
              <div className={classes.institutionSelectWrapper}>
                <Typography variant='headlineMedium' className={classes.accountEligibleTitle}>
                  {selectedGroup?.institution?.name}
                </Typography>
                <Typography variant='paragraphRegular' className={classes.accountEligibleNumber}>
                  {`${selectedGroup?.eligible?.length} account${
                    selectedGroup?.eligible?.length === 1 ? '' : 's'
                  } eligible`}
                </Typography>
                <InstitutionErrorBanner
                  institutionLogin={getInstitutionLoginInErrorByInstId(selectedGroup?.institution?.get('id') || '')}
                  location='onboarding for transfer flow'
                  className={classes.errorBanner}
                />
                {Boolean(renderAccountListForInstitution.eligibleAccounts.length) && (
                  <Typography variant='bodyRegular' className={classes.accountEligibleText}>
                    {' '}
                    Accounts eligible for transfers{' '}
                  </Typography>
                )}

                {renderAccountListForInstitution.eligibleAccounts}

                <div className={classes.buttonWrapper}>
                  <QButton onClick={handleRequestCreate} className={classes.endButton} startIcon={<AddCircleIcon />}>
                    account
                  </QButton>
                </div>

                {Boolean(renderAccountListForInstitution.ineligibleAccounts.length) && (
                  <Typography variant='bodyRegular' className={classes.accountEligibleText}>
                    {' '}
                    All other accounts{' '}
                  </Typography>
                )}
                {renderAccountListForInstitution.ineligibleAccounts}
              </div>
            </div>
          )
        }
        {tabIndex === onboardA2ADialogIndexEnums.ACCT_INFO && mainView === 'READY' && (
          <div className={classes.viewOneWrapper}>
            <div className={classes.instituteIconWrapper}>
              <InstitutionIcon institution={selectedGroup?.institution || null} />
            </div>
            <div className={classes.institutionSelectWrapper}>
              <Typography variant='titleMedium' className={classes.accountEligibleTitle}>
                {selectedGroup?.institution?.name}
              </Typography>
              <Typography variant='paragraphRegular' className={classes.accountEligibleNumber}>
                {currentAccount ? getFinalFourFormatted(currentAccount, false) : 'Unknown Account Type'}
              </Typography>
              <div className={classes.accountFormWrapper}>
                <Typography variant='bodyRegular' className={classes.accountEligibleNumber}>
                  {'To enable transfers, enter this account’s routing and account numbers.'}
                </Typography>
                <div className={classes.accountWrapper}>
                  <Typography variant='captionRegular' className={classes.secondaryText}>
                    {' '}
                    Account{' '}
                  </Typography>
                  <Dump obj={currentAccount} />
                  <Typography variant='bodyRegular'>{getFinalFourFormatted(currentAccount, true)}</Typography>
                </div>

                <Typography variant='titleRegular' color='textSecondary' className={classes.accountDetailsHeader}>
                  {' '}
                  Enter account details{' '}
                </Typography>
                <SelectAccountForm
                  key={`select-account-${currentAccount?.id}`}
                  account={selectedAccountValidationObj}
                  accountNumber={selectedAccountValidationObj?.accountNumber}
                  routingNumber={selectedAccountValidationObj?.routingNumber}
                  accountNumberConfirm={selectedAccountValidationObj?.accountNumberConfirm}
                  onChange={handleChange}
                  fieldErrors={fieldErrors}
                  institution={selectedGroup?.institution}
                />
              </div>
            </div>
          </div>
        )}
        {
          // Confirm the information you entered is correct screen (DWOLLA)
          tabIndex === onboardA2ADialogIndexEnums.SUBMIT && mainView === 'READY' && (
            <Box p={2} pl={3} pr={3}>
              <Typography variant='headlineRegular'>You&apos;re almost done!</Typography>
              <Typography variant='bodyRegular'>
                Two small amounts (less than $1 each) will be deposited to verify your account.
              </Typography>
              <Divider className={classes.divider} />
              <Typography color='textSecondary' variant='titleRegular'>
                Verify Details
              </Typography>
              <div className={classes.detailsWrapper}>
                <div className={classes.accountWrapper}>
                  <Typography variant='captionRegular' className={classes.secondaryText}>
                    {' '}
                    Account{' '}
                  </Typography>
                  <Typography variant='bodyRegular'>{getFinalFourFormatted(currentAccount, true)}</Typography>
                </div>
                <div className={classes.insideWrapper}>
                  <div className={classes.accountNumberWrapper}>
                    <Typography variant='captionRegular' className={classes.secondaryText}>
                      {' '}
                      Account number{' '}
                    </Typography>
                    <Typography variant='bodyRegular'>{selectedAccountValidationObj?.accountNumber}</Typography>
                  </div>
                  <div className={classes.accountWrapper}>
                    <Typography variant='captionRegular' className={classes.secondaryText}>
                      {' '}
                      Routing number{' '}
                    </Typography>
                    <Typography variant='bodyRegular'>{selectedAccountValidationObj?.routingNumber}</Typography>
                  </div>
                </div>
                <Box marginTop={1} marginLeft={-1}>
                  {' '}
                  {/* Negative margin to make up for checkbox padding */}
                  <FormControl>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={agreedToTerms}
                          className={classes.checkbox}
                          onChange={() => {
                            if (!agreedToTerms) {
                              tracker.track(tracker.events.authorizeTransfer);
                            }
                            toggleAgreedToTerms();
                          }}
                        />
                      }
                      label={
                        <Typography className={classes.secondaryText} component='span' variant='paragraphRegular'>
                          I authorize deposits to be sent to this account in accordance with the&nbsp;
                          <Typography className={classes.termsLink} variant='inherit' onClick={handleOpenTerms}>
                            Simplifi Terms of Service
                            <OpenInNewIcon fontSize='inherit' />.
                          </Typography>
                        </Typography>
                      }
                    />
                  </FormControl>
                </Box>
              </div>
            </Box>
          )
        }
        {
          //  Note: the MoneyInBankImage is included because it takes a fraction of a second longer to render it than the rest of the text
          tabIndex === onboardA2ADialogIndexEnums.SUCCESS && mainView === 'READY' && MoneyInBankImage && (
            <Box
              pt={2}
              pb={2}
              pl={4}
              pr={4}
              display='flex'
              flexDirection='column'
              alignItems='center'
              textAlign='center'
            >
              <img src={MoneyInBankImage} alt='money-movement' className={classes.finalImage} />
              <Typography variant='displayRegular' className={classes.pendingTitle}>
                Verification deposits on the way!
              </Typography>
              <Typography variant='bodyRegular' className={classes.secondaryTextItem}>
                Two small amounts will be deposited into this account within a day or two.
              </Typography>
              <Typography variant='bodyRegular' className={classes.secondaryTextItem}>
                {'You’ll then be notified on how to complete account verification.'}
              </Typography>
            </Box>
          )
        }
        {
          //  Note: the MoneyMovementImage is included because it takes a fraction of a second longer to render it than the rest of the text
          tabIndex === onboardA2ADialogIndexEnums.FAILURE &&
            (mainView === 'ERROR' || mainView === 'READY') &&
            MoneyInBankImage && (
              <ZeroStateView
                primaryVariant='displayRegular'
                secondaryVariant='bodyRegular'
                id='microdeposit-failure'
                primary='Account verification failed'
                secondary={
                  'We’re sorry, but verification deposits could not be delivered to this account. Please go to the Help Center, and initiate a chat with one of our support agents.'
                }
                icon={paperAirplaneCrash}
              />
            )
        }
        {
          // If the funding account already exists and is already verified and then reconnects itself to the login account (DWOLLA) OR if the fundingAccount is an instant set up account (INTUIT WIDGET)
          tabIndex === onboardA2ADialogIndexEnums.READY_TO_TRANSFER && mainView === 'READY' && (
            <Box className={classes.successTab}>
              <img className={classes.thumbsUp} alt='thumbs-up' src={GreenThumbImage} />
              <Box mt={3} mb={3}>
                <Typography variant='headlineMedium'>Account ready for transfers!</Typography>
              </Box>
              <Typography variant='bodyRegular' color='textSecondary'>
                You can now transfer funds into this account in Simplifi! Transfer funds now or anytime using the{' '}
                <b>Transfer Money</b> button at the top of the<b>Holdings</b> and <b>Transactions</b> page.
              </Typography>
            </Box>
          )
        }
        {mainView === 'ERROR' && tabIndex === onboardA2ADialogIndexEnums.ERROR && (
          <Box className={classes.errorTab}>
            <ZeroStateView
              primaryVariant='displayRegular'
              id='microdeposit-failure'
              primary={'Failed to enable transfers'}
              secondary={
                <span className={classes.errorWrapper}>
                  {errorMessage}
                  {errorCode && <Typography className={classes.errorCodeText}>{`Error Code: ${errorCode}`}</Typography>}
                </span>
              }
              icon={paperAirplaneCrash}
            />
          </Box>
        )}
        {mainView === 'LOADING' && (
          <Box
            paddingTop={20}
            paddingBottom={20}
            display='flex'
            alignItems='center'
            flexDirection='column'
            justifyContent='center'
          >
            {' '}
            <LoadingView size={100} />
          </Box>
        )}
        {mainView === 'FAILED' && (
          <ZeroStateView
            primaryVariant='displayRegular'
            secondaryVariant='bodyRegular'
            className={classes.errorTab}
            button={
              <QButton id={'try-again-button'} variant='outlined' onClick={getFundingAccounts}>
                TRY AGAIN
              </QButton>
            }
            id='failed-to-fetch'
            primary='Unable to fetch data'
            secondary="We can't fetch your data from the server. Please try again"
            icon={paperAirplaneCrash}
          />
        )}
        {mainView === 'EMPTY' && showEmptyView}
      </StdDialogContent>
      {mainView === 'READY' &&
        (tabIndex === onboardA2ADialogIndexEnums.OPENING ||
          tabIndex === onboardA2ADialogIndexEnums.ACCT_INFO ||
          tabIndex === onboardA2ADialogIndexEnums.SUBMIT ||
          tabIndex === onboardA2ADialogIndexEnums.FAILURE) && (
          <StdDialogActions
            primary={{
              disabled: disableContinue,
              children: continueText,
              onClick: handleContinue,
              variant: tabIndex === onboardA2ADialogIndexEnums.FAILURE ? 'outlined' : 'contained',
            }}
          />
        )}
      {mainView === 'READY' && tabIndex === onboardA2ADialogIndexEnums.READY_TO_TRANSFER && (
        <StdDialogActions
          primary={{
            children: 'Transfer Funds',
            onClick: kickoffTransferFlow,
            variant: 'contained',
          }}
          secondary={{
            children: 'Close',
            onClick: handleClose,
            variant: 'outlined',
          }}
        />
      )}
      {mainView === 'READY' && tabIndex === onboardA2ADialogIndexEnums.SUCCESS && (
        <StdDialogActions
          primary={{
            disabled: disableContinue,
            children: continueText,
            onClick: handleContinue,
            variant: 'contained',
          }}
          secondary={{
            children: 'set up another account',
            variant: 'outlined',
            onClick: () => {
              setSelectedInstitutionId(null);
              setSelectedAccountValidationObj(null);
              setTabIndex(onboardA2ADialogIndexEnums.SELECT_INST);
            },
          }}
        />
      )}
      {mainView === 'ERROR' && (
        <StdDialogActions
          primary={{
            children: 'close',
            onClick: handleClose,
            variant: 'outlined',
          }}
        />
      )}
    </StdDialog>
  );
});

OnboardAccountsForTransfersDialog.displayName = 'OnboardAccountsForTransfersDialog';

export default OnboardAccountsForTransfersDialog;
